/**
 * Demo-mode bridge answers (docs/widget-gallery-page.md §1).
 *
 * The gallery's mock host answers every tools/call a widget makes from
 * INSIDE the page — no network, no tenant endpoints:
 *
 *   - the widget's paired data tool (Refresh / auto-refresh / the
 *     refresh-on-load session_status) re-delivers the current fixture
 *   - mutating tools (stop_session, respond_to_prompt, ask_agent,
 *     retry_job, create_schedule, update_schedule, set_schedule_enabled)
 *     resolve to a visible demo-mode response — most also nudge the
 *     fixture so the follow-up refresh tells a coherent story (the stop
 *     winds the card down, the prompt response clears the needs row,
 *     the retry mints a queued job)
 *   - anything else resolves to a JSON-RPC error naming demo mode
 *
 * Results use the real ToolResult envelope ({content, structuredContent})
 * the widgets' bridgeOutput() unwraps. Small delays keep the widgets'
 * in-flight states ("re-fetching live status…", "asking…", "saving…")
 * visible, like a real round-trip would.
 */
import {
  applyDemoResponse,
  applyDemoStop,
  type SessionStatusFixture,
} from "./sessionStatus";
import { applyDemoRetry, type QueueFixture } from "./queue";

export type DemoAnswer =
  | { delayMs: number; result: Record<string, unknown> }
  | { delayMs: number; errorMessage: string };

/** Wrap structured data in the MCP ToolResult envelope widgets expect. */
function toolResult(structured: Record<string, unknown>) {
  return {
    content: [{ type: "text", text: JSON.stringify(structured) }],
    structuredContent: structured,
  };
}

export function answerDemoToolCall(
  pairedTool: string,
  fixture: Record<string, unknown>,
  name: string,
  args: Record<string, unknown>
): DemoAnswer {
  // Refresh / auto-refresh: re-deliver the (possibly nudged) fixture.
  if (name === pairedTool) {
    return { delayMs: 450, result: toolResult(fixture) };
  }

  switch (name) {
    case "stop_session": {
      applyDemoStop(fixture as SessionStatusFixture);
      return {
        delayMs: 500,
        result: toolResult({
          sessionId: args.sessionId ?? null,
          agent: fixture.agent ?? null,
          stopping: true,
        }),
      };
    }
    case "respond_to_prompt": {
      const response = String(args.response ?? "");
      applyDemoResponse(fixture as SessionStatusFixture, response);
      return {
        delayMs: 600,
        result: toolResult({ delivered: true, response, demo: true }),
      };
    }
    case "ask_agent": {
      const agent = String(args.agent ?? "the agent");
      return {
        delayMs: 1100,
        result: toolResult({
          delivered: true,
          sessionId: "ses-demo-ask",
          agent,
          routed: "existing",
          status: "running",
          reply:
            "Demo mode — this gallery answers locally. Connected to your " +
            `own swarm, ${agent} would reply here in a few seconds.`,
          teamRef: null,
        }),
      };
    }
    case "retry_job": {
      const structured = applyDemoRetry(
        fixture as QueueFixture,
        String(args.jobId ?? "")
      );
      return { delayMs: 700, result: toolResult(structured) };
    }
    case "create_schedule": {
      return {
        delayMs: 650,
        result: toolResult({
          scheduleId: `demo-${Math.random().toString(36).slice(2, 8)}`,
        }),
      };
    }
    case "update_schedule": {
      return {
        delayMs: 650,
        result: toolResult({ scheduleId: args.scheduleId ?? null }),
      };
    }
    case "set_schedule_enabled": {
      return {
        delayMs: 300,
        result: toolResult({
          scheduleId: args.scheduleId ?? null,
          enabled: args.enabled === true,
        }),
      };
    }
    default:
      return {
        delayMs: 200,
        errorMessage: `demo mode — ${name} is simulated in this gallery`,
      };
  }
}
